import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getSimpleImageUrl } from 'lib/image/imageUtils'

interface NavSection {
  label: string
  href: string
}

function getNavSections(
  regionCode: string,
  eventId: string,
  intl: IntlShape,
): Array<NavSection> {
  const base = `/${regionCode}/corporate-events/${eventId}`
  return [
    {
      label: intl.formatMessage({
        id: 'Vl-e-WQ_DOFUz-HZBUds',
        defaultMessage: 'Home',
        description: 'Navigation label for the corporate event landing page',
      }),
      href: base,
    },
    {
      label: intl.formatMessage({
        id: 'UTjtk16iZuD0AbfMj1wX',
        defaultMessage: 'Things to know',
        description: 'Navigation label for the corporate event things to know page',
      }),
      href: `${base}/things-to-know`,
    },
    {
      label: intl.formatMessage({
        id: 'zLUndq_-MqU64_JE78KM',
        defaultMessage: 'Flights',
        description: 'Navigation label for the corporate event flights page',
      }),
      href: `${base}/flights`,
    },
    {
      label: intl.formatMessage({
        id: 'Z-IQSsWfmVuM6OUO0SYG',
        defaultMessage: 'Accommodation',
        description: 'Navigation label for the corporate event accommodation page',
      }),
      href: `${base}/accommodation`,
    },
    {
      label: intl.formatMessage({
        id: 'lLBjK4riqApmfiPe-Wh2',
        defaultMessage: 'Optional Activities',
        description: 'Navigation label for the corporate event optional activities page',
      }),
      href: `${base}/other-activities`,
    },
    {
      label: intl.formatMessage({
        id: 'N41vpmYASeYplCX-4x9K',
        defaultMessage: 'FAQs and T&Cs',
        description: 'Navigation label for the corporate event FAQ and terms page',
      }),
      href: `${base}/faq`,
    },
  ]
}

const Nav = styled.nav`
  z-index: 100;
  width: 100%;
  background: ${(props) => props.theme.palette.neutral.default.eight};

  ${mediaQueryUp.desktop} {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(30px);
    border-bottom: ${rem(1)} solid
      ${(props) => props.theme.palette.neutral.default.five};
  }
`

const MobileTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(8)} ${rem(12)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const MobileBottom = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  padding: 0 ${rem(12)};
  border-bottom: ${rem(1)} solid
    ${(props) => props.theme.palette.neutral.default.five};
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DesktopInner = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${rem(18)} ${rem(32)} ${rem(18)} ${rem(20)};
  }
`

const MobileLogo = styled.img`
  max-width: ${rem(162)};
  height: auto;
  object-fit: contain;
  display: block;
`

const DesktopLogo = styled.img`
  height: ${rem(36)};
  width: auto;
  display: block;
`

const MobileNavLink = styled.a`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  color: ${(props) => props.theme.palette.neutral.default.one};
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  padding: ${rem(10)} ${rem(12)};

  &.is-first {
    padding-inline-start: 0;
  }
`

const DesktopNavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

const DesktopNavLink = styled.a`
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: ${(props) => props.theme.palette.neutral.default.one};
  text-decoration: underline;
  text-decoration-color: ${(props) => props.theme.palette.neutral.default.one};
  text-decoration-thickness: ${rem(1)};
  text-underline-offset: ${rem(6)};
  white-space: nowrap;
  padding: ${rem(12)} ${rem(16)};

  &.is-active {
    color: #666d82;
    text-decoration-color: #666d82;
  }
`

const RegisterButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(20)};
  color: ${(props) => props.theme.palette.neutral.default.eight};
  background: ${(props) => props.theme.palette.neutral.default.one};
  text-decoration: none;
  padding: ${rem(10)} ${rem(12)};
  border-radius: ${rem(1000)};
  white-space: nowrap;

  ${mediaQueryUp.desktop} {
    font-size: ${rem(16)};
    line-height: ${rem(24)};
    padding: ${rem(12)} ${rem(16)};
  }
`

interface Props {
  eventId: string
  regionCode: string
}

function CorporateEventNavbar({ eventId, regionCode }: Props) {
  const intl = useIntl()
  const { pathname } = useLocation()
  const sections = useMemo(() => getNavSections(regionCode, eventId, intl), [eventId, intl, regionCode])
  const registerLabel = intl.formatMessage({
    id: 'a8Wo9VA6HxuMPHKyrgDl',
    defaultMessage: 'Register',
    description: 'Button label linking to corporate event registration',
  })

  return (
    <Nav>
      <MobileTop>
        <MobileLogo
          src={getSimpleImageUrl('cplg5940koezrayr6js3', 'png')}
          alt="7Platinum"
        />
        <RegisterButton
          href={`/${regionCode}/corporate-events/${eventId}/register`}
        >
          {registerLabel}
        </RegisterButton>
      </MobileTop>

      <MobileBottom>
        {sections.map((section, i) => (
          <MobileNavLink
            key={section.href}
            href={section.href}
            className={i === 0 ? 'is-first' : undefined}
          >
            {section.label}
          </MobileNavLink>
        ))}
      </MobileBottom>

      <DesktopInner>
        <DesktopLogo
          src={getSimpleImageUrl('cplg5940koezrayr6js3', 'png')}
          alt="7Platinum"
        />
        <DesktopNavLinks>
          {sections.map((section) => (
            <DesktopNavLink
              key={section.href}
              href={section.href}
              className={pathname === section.href ? 'is-active' : undefined}
            >
              {section.label}
            </DesktopNavLink>
          ))}
          <RegisterButton
            href={`/${regionCode}/corporate-events/${eventId}/register`}
          >
            {registerLabel}
          </RegisterButton>
        </DesktopNavLinks>
      </DesktopInner>
    </Nav>
  )
}

export default CorporateEventNavbar
